// Claim submission service for the public /submit-claim page. Talks to the
// motor-assessment-api claims endpoints; JSON and multipart share one path.
import axios from 'axios'
import { toClaimSubmission } from './claim-form-mapper'

export const CLAIM_ENDPOINTS = {
  submit: '/motor-assessment/api/claims/submit',
  windscreenSubmit: '/motor-assessment/api/claims/windscreen/submit',
  // Bot-protection gate: the backend verifies the Cloudflare Turnstile token
  // server-side here, before the form is rendered. This is the only Turnstile
  // interaction in the flow — submissions carry no bot-protection headers.
  verifyGate: '/motor-assessment/api/claims/verify-gate',
  attachmentUpload: '/motor-assessment/api/claims/attachments/upload',
  initiateClaim: '/motor-assessment/api/claims/initiate-claim',
  // Speech-to-text; not under /api/claims — it's unattached to any claim record.
  transcribeAudio: '/motor-assessment/api/transcription/audio',
  structuredExtraction: '/motor-assessment/api/transcription/structured',
  // The blank Motor Claim Form hosted on the company website.
  templateUrl: 'https://www.apainsurance.org/pdf/claim/motor_claim_form.pdf',
}

// Verify the Turnstile token at the gate. Resolves to the backend Result
// ({ success, msg }); success:true means the user cleared the gate and the SPA
// may render the claim form.
export function verifyGate(token) {
  return axios.post(CLAIM_ENDPOINTS.verifyGate, null, {
    headers: { 'cf-turnstile-response': token },
  })
}

// Upload a single claim attachment (photo, document or sketch) to S3 before
// the final submission. Returns { success, data: { type, url }, msg }.
// Each file is sent as its own request so no upload breaches the gateway limit.
// Pass claimId to also attach the file to an existing claim (e.g. a late police abstract).
export function uploadClaimAttachment(file, regNo, attachmentType, claimId) {
  const form = new FormData()
  form.append('file', file)
  form.append('regNo', regNo)
  form.append('attachmentType', attachmentType)
  if (claimId) form.append('claimId', claimId)
  return axios.post(CLAIM_ENDPOINTS.attachmentUpload, form)
}

// Online-filled claim: submitted as JSON. Collected S3 URLs from prior
// uploadClaimAttachment calls are passed in payload.attachments.
export function submitClaimForm(payload) {
  const submission = toClaimSubmission(payload.form, payload.attachments)
  return axios.post(CLAIM_ENDPOINTS.submit, {
    header: submission.header,
    body: {
      ...submission.body,
      origin: 'FORM',
    },
  })
}

// Uploaded completed form: submitted as multipart. The `claim` part carries the
// claimId (from initiateClaim, so this merges into that same DRAFT rather than
// staging a second record) plus identifying header as JSON, and an optional
// body with pre-uploaded attachment URLs; the report body itself is read from
// the attached file server-side.
// payload: { claimId?, file, vehicleRegistrationNumber, insuredMobileNumber, attachments?, origin? }
export function submitUploadedClaim(payload) {
  const claim = {
    claimId: payload.claimId || undefined,
    header: {
      regNo: payload.vehicleRegistrationNumber?.trim(),
      phoneNumber: payload.insuredMobileNumber?.trim(),
    },
    body: {
      ...(payload.attachments?.length ? { attachments: payload.attachments } : {}),
      origin: payload.origin || 'UPLOAD',
    },
  }
  const form = new FormData()
  form.append(
    'claim',
    new Blob([JSON.stringify(claim)], { type: 'application/json' })
  )
  form.append('file', payload.file)
  return axios.post(CLAIM_ENDPOINTS.submit, form)
}

// Look up a vehicle's policy on GIS and open a DRAFT claim for it — the first
// step of the new async motor-claim wizard. Resolves to
// { success, data: { id, claimReference }, msg }: `id` tracks this claim
// through the rest of the wizard; `claimReference` is the GIS policy number
// (not the final claim reference, which is only assigned on submission).
// success:false (still HTTP 200) when GIS has no policy for the reg number —
// deliberately returns no vehicle/insured details, since this is a public,
// rate-limited endpoint and must not leak another policyholder's data.
export function initiateClaim(regNo, submissionContactNumber) {
  return axios.post(CLAIM_ENDPOINTS.initiateClaim, {
    regNo,
    submissionContactNumber,
  })
}

// Transcribe one recorded answer — transcript at data.content, failures as success:false on HTTP 200.
export function transcribeAudio(recording, prompt) {
  const form = new FormData()
  // named explicitly: a MediaRecorder Blob has none, and the backend reads the extension
  form.append('file', recording.blob, recording.filename)
  if (prompt) form.append('prompt', prompt)
  return axios.post(CLAIM_ENDPOINTS.transcribeAudio, form, {
    timeout: 180000, // audio inference outruns axios' default; the gateway alone allows 120s
  })
}

// Project transcribed text onto a caller-supplied JSON schema — the
// structured counterpart to transcribeAudio. Populated schema at
// data.data.data (StructuredExtractionResult.data), failures as
// success:false on HTTP 200, same contract as transcribeAudio.
export function extractStructured(text, schema, prompt) {
  return axios.post(CLAIM_ENDPOINTS.structuredExtraction, {
    text,
    schema,
    prompt,
  })
}

// Single-call submission for the typed/voice wizard, once all screens are
// filled and persisted locally (see claimDraft.js) — no per-screen network
// round trip. Pass the claimId from initiateClaim so this merges into that
// same DRAFT (keeping the GIS prefill) instead of staging a second record;
// header is included for parity with the pre-claimId contract but is only
// load-bearing if claimId is omitted. Resolves to
// { success, data: { id, claimReference, regNo, phoneNumber, status,
// origin, sourceDocumentS3Url }, msg }.
export function submitClaim({ claimId, header, body }) {
  return axios.post(CLAIM_ENDPOINTS.submit, { claimId, header, body })
}

// Submits the windscreen claim details along with the generated claim ID.
// Payload expects: { claimRecordId, regNo, location: { longitude, latitude }, fittingTime: { day, time }, fittingLocation }
// Resolves to { success, msg, data: { id, claimRecordId, regNo, phoneNumber, ... } }
export function submitWindscreenClaim(payload) {
  return axios.post(CLAIM_ENDPOINTS.windscreenSubmit, payload)
}

export function getTemplateUrl() {
  return CLAIM_ENDPOINTS.templateUrl
}
